import React, { useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import InlineAlert from "../../Utility/InlineAlert/InlineAlert";

import logoImage from "../../assets/images/logoNew.jpg";
import UseTitle from "../../Utility/UseTitle/UseTitle";
import Typography from "../../Utility/Typography";
import Label from "../../Utility/Label/Label";
import Button from "../../Utility/Button/Button";
import FormSubmitButton from "../../Utility/Button/FormSubmitButton";
import PasswordInput from "../../Utility/PasswordToggle/PassswordInput";

import { post } from "../../Services/ApiBase";
import { API_ENDPOINTS } from "../../Services/ApiEndPoints";
import { ResetPasswordSchema } from "../../FormsValidations/Validations";
import { logger } from "../../utils/logger";
import { ROUTES } from "../../constants";

export default function ResetPassword() {
  const location = useLocation();
  const navigate = useNavigate();

  const token = useMemo(() => {
    const sp = new URLSearchParams(location.search);
    return sp.get("token") || "";
  }, [location.search]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(ResetPasswordSchema),
    defaultValues: {
      newPassword: "",
      confirmPassword: "",
    },
  });

  const [loading, setLoading] = useState(false);
  const [localError, setLocalError] = useState("");
  const [success, setSuccess] = useState(false);

  const onSubmit = async (form) => {
    try {
      setLocalError("");
      if (!token) {
        setLocalError("Reset token is missing or invalid.");
        return;
      }

      setLoading(true);
      await post(API_ENDPOINTS.AUTH.RESET_PASSWORD, {
        token,
        newPassword: form.newPassword,
      });
      setSuccess(true);
    } catch (err) {
      logger.error("Reset password failed:", err);
      const apiMsg = err?.response?.data?.data?.message ?? err?.response?.data?.message;
      setLocalError(apiMsg || err?.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <UseTitle formattedTitle="Reset Password" />
      <div className="auth-page-wrapper">
        <div className="auth-page-content pb-0">
          <div className="w-full max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-center h-screen">
              <div className="w-full md:w-2/3 lg:w-1/2 xl:w-2/5">
                <div className="bg-surface-primary rounded-md border border-border-primary shadow-sm mt-4">
                  <div className="mt-5">
                    <div className="flex justify-center items-center">
                      <img
                        src={logoImage}
                        alt="Logo"
                        className="h-[72px] w-auto max-w-[280px] object-contain"
                      />
                    </div>
                  </div>

                  <div className="p-4 mt-4">
                    {localError && (
                      <InlineAlert className="mb-3" severity="error">
                        {localError}
                      </InlineAlert>
                    )}
                    {success && (
                      <InlineAlert className="mb-3" severity="success">
                        Your password has been reset successfully.
                      </InlineAlert>
                    )}

                    {!success ? (
                      <div className="p-2">
                        <Typography variant="h4" className="text-center mb-4 text-theme-primary">
                          Reset Password
                        </Typography>

                        <form onSubmit={handleSubmit(onSubmit)}>
                          <div className="mb-3">
                            <Label htmlFor="new-password">Password</Label>
                            <div className="relative mb-1">
                              <PasswordInput
                                register={register}
                                name="newPassword"
                                id="new-password"
                              />
                            </div>
                            <Typography
                              variant="text2"
                              className="block w-full mt-1 text-badge-danger"
                            >
                              {errors.newPassword?.message}
                            </Typography>
                          </div>

                          <div className="mb-3">
                            <Label htmlFor="confirm-password">Confirm Password</Label>
                            <div className="relative mb-1">
                              <PasswordInput
                                register={register}
                                name="confirmPassword"
                                id="confirm-password"
                              />
                            </div>
                            <Typography
                              variant="text2"
                              className="block w-full mt-1 text-badge-danger"
                            >
                              {errors.confirmPassword?.message}
                            </Typography>
                          </div>

                          <div className="mt-4">
                            <FormSubmitButton
                              loading={loading}
                              label="Reset Password"
                              loadingText="Resetting..."
                              idleIconClass=""
                              className="w-full flex items-center justify-center gap-2"
                            />
                          </div>
                        </form>

                        <div className="mt-4 text-center">
                          <Link
                            to={ROUTES.LOGIN}
                            className="text-text-secondary hover:text-theme-primary transition-colors text-sm"
                          >
                            Back to Login
                          </Link>
                        </div>
                      </div>
                    ) : (
                      <div className="p-2 text-center">
                        <Typography variant="text2" className="mb-6 text-gray-600">
                          You can now log in with your new password.
                        </Typography>
                        <Button
                          type="button"
                          variant="primary"
                          className="w-full flex items-center justify-center gap-2"
                          onClick={() => navigate(ROUTES.LOGIN)}
                        >
                          <Typography variant="text2" component="span">
                            Go to Login
                          </Typography>
                        </Button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-4 text-center"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
